import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * Indicatore che scorre sotto l'elemento attivo, invece di comparire di scatto
 * sotto ognuno: rende evidente da dove a dove ci si e' spostati.
 */
function useSliderPosition(activeKey: string) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const active = container.querySelector<HTMLElement>('[data-active="true"]');
    if (!active) { setStyle(null); return; }
    setStyle({ left: active.offsetLeft, width: active.offsetWidth });
  }, [activeKey]);

  return { containerRef, style };
}

export interface TabItem<K extends string = string> {
  key: K;
  label: string;
  icon?: React.ReactNode;
}

/** Tab della destinazione del collegamento (Cliente/Lead, Offerta, Progetto). */
export function TabBar<K extends string>({ items, active, onChange }: {
  items: TabItem<K>[];
  active: K;
  onChange: (key: K) => void;
}) {
  const { containerRef, style } = useSliderPosition(active);

  return (
    <div ref={containerRef} className="relative flex items-center gap-0.5">
      {items.map(item => (
        <button
          key={item.key}
          type="button"
          data-active={active === item.key}
          onClick={() => onChange(item.key)}
          className={`px-3 py-2 text-[13px] font-medium transition-colors duration-150 flex items-center gap-1.5 rounded-t-md ${
            active === item.key
              ? 'text-[#37352F] dark:text-white'
              : 'text-[#787774] dark:text-white/40 hover:text-[#37352F] dark:hover:text-white hover:bg-black/[0.03] dark:hover:bg-white/[0.04]'
          }`}
        >
          {item.icon}{item.label}
        </button>
      ))}
      {style && (
        <span
          aria-hidden
          className="mail-link-slider absolute -bottom-px h-0.5 bg-[#2383E2] rounded-full"
          style={{ transform: `translateX(${style.left}px)`, width: style.width }}
        />
      )}
    </div>
  );
}

/** Sotto-tab "Associa esistente" / "Crea nuovo". */
export function SubTabs<K extends string>({ items, active, onChange }: {
  items: TabItem<K>[];
  active: K;
  onChange: (key: K) => void;
}) {
  const { containerRef, style } = useSliderPosition(active);

  return (
    <div ref={containerRef} className="relative flex items-center gap-3 pb-1.5">
      {items.map(item => (
        <button
          key={item.key}
          type="button"
          data-active={active === item.key}
          onClick={() => onChange(item.key)}
          className={`text-xs font-medium transition-colors duration-150 ${
            active === item.key
              ? 'text-[#2383E2]'
              : 'text-[#787774] dark:text-white/40 hover:text-[#37352F] dark:hover:text-white'
          }`}
        >
          {item.label}
        </button>
      ))}
      {style && (
        <span
          aria-hidden
          className="mail-link-slider absolute bottom-0 h-0.5 bg-[#2383E2] rounded-full"
          style={{ transform: `translateX(${style.left}px)`, width: style.width }}
        />
      )}
    </div>
  );
}

/**
 * Selettore a segmenti con pastiglia che scorre (Solo persona / Solo azienda /
 * Azienda + persona).
 */
export function SegmentedControl<K extends string>({ items, active, onChange }: {
  items: TabItem<K>[];
  active: K;
  onChange: (key: K) => void;
}) {
  const { containerRef, style } = useSliderPosition(active);

  return (
    <div
      ref={containerRef}
      className="relative flex items-center gap-1 p-0.5 rounded-lg bg-[rgba(55,53,47,0.05)] dark:bg-white/5"
    >
      {style && (
        <span
          aria-hidden
          className="mail-link-slider absolute top-0.5 bottom-0.5 rounded-md bg-white dark:bg-gray-800 shadow-sm"
          style={{ transform: `translateX(${style.left - 2}px)`, width: style.width }}
        />
      )}
      {items.map(item => (
        <button
          key={item.key}
          type="button"
          data-active={active === item.key}
          onClick={() => onChange(item.key)}
          className={`relative z-[1] flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors duration-150 ${
            active === item.key
              ? 'text-[#37352F] dark:text-white'
              : 'text-[#787774] dark:text-white/40 hover:text-[#37352F] dark:hover:text-white'
          }`}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

/** Tasto mostrato accanto a un'azione, per dichiarare la scorciatoia da tastiera. */
export function KeyHint({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded border border-black/[0.12] dark:border-white/20 bg-black/[0.03] dark:bg-white/[0.06] text-[10px] font-sans font-medium text-[#787774] dark:text-white/50">
      {children}
    </kbd>
  );
}
